import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { message, Upload } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { T } from '../../components/Translations';
import { hideLoader, showLoader } from '../Loader';
import store from '../../store';
import { Ajax } from '../../components/Ajax';
import PropTypes from 'prop-types';

const maxcount = 100;

function ImageWithAuth(props){
    const [src, setSrc] = useState();

    useEffect(() => {
        props.src && Ajax.downloadImage({url: props.src, //NOSONAR
            success: function (imageUrl) {
                setSrc(imageUrl);
            }
        });
    }, [props.src]) //NOSONAR

    return props.src && <img alt="" //NOSONAR
        {...props}
        src={src}
    />
}

ImageWithAuth.propTypes = {
    /** Source URL of the image to download and display */
    src: PropTypes.string,
    /** Alt text for the image */
    alt: PropTypes.string,
    /** Additional image attributes (style, className, etc.) */
    style: PropTypes.object
};

const api_url = process.env.REACT_APP_DEV_API_URL ?? process.env.REACT_APP_API_URL;
const api_app = process.env.REACT_APP_DEV_API_APP ?? process.env.REACT_APP_API_APP;

/**
 * the component used in forms for image upload
 * @param {any} props contains: imageUrl, imageField, dataItem, setDataItem, type, alt, showUploadList, listType, className, actionUrl
 * @exports CustomUpload
 */
export default function CustomUpload(props) {
    const url = (api_url + api_app || "");
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const { 
        imageField, dataItem, setDataItem, type, alt,
        showUploadList, listType, fileNameField, className,
        actionUrl, multiple, skipDeleteIcon, keepName,
        showList, anyFile, hideLink, isLocalUpload, isManifest
    } = props;
    const [fileList, setFileList] = useState([]);
    const duplicateNames = useRef([]);
    let uploadingCount = useRef(0);
    const uploadRef = useRef(null);

    const customRequest = useCallback(event => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(event.file);
        fileReader.onload = () => {
            event.onSuccess(fileReader.result);
        }
        fileReader.onerror = (error) => {
            event.onError(error);
        }
    }, []);

    useEffect(() => {
        return () => hideLoader("upload");
    }, []);

    useEffect(() => {
        if (fileList.length) {
            let newData = { ...dataItem };
            let image_url;

            if (multiple) {
                newData[imageField] = [...newData[imageField], ...fileList.map(f => {
                    image_url = url + "/Images/GetFile/" + (f.name || '') + "?type=Temp&filename=" + f.response.FileName
                    return { image_guid: f.response.FileName, name: f.name, image_url }
                })];

            } else if (isLocalUpload) {
                if (isManifest) {
                    image_url = fileList[0].response;
                    
                    newData[imageField] = { ...newData[imageField], src: image_url, type: fileList[0].type };
                } else {
                    image_url = fileList[0].response;
                    newData[imageField] = image_url;
                }
            } else {
                image_url = url + "/Images/GetFile/" + (fileList[0].name || '') + "?type=Temp&filename=" + fileList[0].response.FileName;
                newData[imageField] = [{ image_guid: fileList[0].response.FileName, name: fileList[0].name, image_url }];
            }

            // image_url = url + "/Images/GetFile?type=Temp&filename=" + fileList[0].response.FileName + "&fileDownloadName=" + (fileList[0].name || '')
            // newData[imageField] = [{ image_guid: fileList[0].response.FileName, name: fileList[0].name, image_url }]
            setDataItem(newData);
            setFileList([]);
        }
    }, [fileList, keepName]);// eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = useCallback((info) => {
        if (!uploadRef.current) {
            return;
        }

        switch (info.file.status) {
            case 'uploading':
                return;

            case 'error': {
                const responseFileInfo = info.file.response?.File;
                message.error((responseFileInfo?.length && responseFileInfo[0]) || `${t('text.unsupported_media_type')}: "${info.file.name}"`);
                break;
            }

            case 'done':
                if (info.file.response?.HasError) {
                    message.error(info.file.response?.Message);
                    setIsLoading(false);
                    hideLoader("upload");
                    return;
                }
                break;

            default:
                break;
        }

        if (info.fileList.every(f => f.status === 'done' || f.status === 'error' || !f.status)) {
            setFileList(info.fileList.filter(f => f.status === 'done'));
            setIsLoading(false);
            hideLoader("upload");
            if (duplicateNames.current.length) {
                message.warning(<T args={[duplicateNames.current.length]}>message.duplicate_file_name</T>);
                duplicateNames.current = [];
            }
            uploadingCount.current = 0;
        }
    }, [duplicateNames, t]);

    const removeImage = useCallback(image => {
        let newData = { ...dataItem };
        if (multiple) {
            newData[imageField] = newData[imageField].filter(im => im.image_guid !== image);

        } else {
            newData[imageField] = [];

        }
        setDataItem(newData);
        //   }
    }, [dataItem, setDataItem, imageField, multiple]);

    const beforeUpload = useCallback((file) => {
        showLoader("upload");
        setIsLoading(true);
        if (keepName && dataItem[imageField].some(im => im.name === file.name)) {
            duplicateNames.current = [...duplicateNames.current, file.name];
            return false;
        }
        uploadingCount.current += 1;
        if (uploadingCount.current > maxcount) {
            if (uploadingCount.current === maxcount + 1) {
                message.warning(<T args={[maxcount]}>message.only_max_count_file_will_be_processed</T>, 2)
            }
            return false;
        }

        return true;
    }, [dataItem, imageField, keepName]);

    const uploadButton = (
        <div>
            {
                //<Icon type={isLoading ? 'loading' : 'plus'} />
                isLoading ? <LoadingOutlined /> : <PlusOutlined />
            }
            <div className="ant-upload-text"><T>text.upload</T></div>
        </div>
    );

    const innerContent = dataItem[imageField] && (dataItem[imageField][0] || dataItem[imageField].src) && <div className={className || "avatar-uploader"}>
        <span>
            {(anyFile ? (hideLink ? <></> : <a target='_blank' href={dataItem[imageField][0].image_url} >{fileNameField ? dataItem[fileNameField] : (dataItem[imageField][0].name || dataItem[imageField][0].image_guid)}</a>) : /*eslint-disable-line react/jsx-no-target-blank*/
                <ImageWithAuth 
                    src={isLocalUpload ? (isManifest ? dataItem[imageField].src : dataItem[imageField] ): dataItem[imageField][0].image_url
                    } 
                    alt={alt || dataItem[imageField].image_url
                    } 
                    style={{ width: '100%', objectFit: 'contain' }} 
                />
            )}
        </span>
    {skipDeleteIcon ? <></> : <button className="button micro" type='button' onClick={
        (e) => {
            e.stopPropagation();
            removeImage(dataItem[imageField]);
        }
    }>< icon>delete</icon></button >}</div>

    return <>
        {!fileList.length && ((!multiple && innerContent) || <Upload ref={uploadRef}
            accept={anyFile ? '.png, .jpg, .gif, .jpeg, .pdf, .mp4, .mov, .avi, .doc, .docx, .xls, .xlsx' : '.png, .jpg, .gif, .jpeg'}
            listType={listType || "picture-card"}
            multiple={multiple}
            //  fileList={fileList}
            // className={className || "avatar-uploader"}
            headers={
                {
                    authorization: `Bearer ${store.getState().userToken}`,
                    'X-Requested-With': null
                }
            }
            className={className}
            {...(isLocalUpload ? { customRequest } : { action: url + (actionUrl || "/Images/UploadFile?type=" + type) })}
            showUploadList={showUploadList || false}
            //action={url + (actionUrl || "/Images/UploadFile?&type=" + type)}
            beforeUpload={beforeUpload}
            onChange={handleChange}
        >
            {uploadButton}
        </Upload >)}
        {multiple && showList ? dataItem[imageField].map((m, i) => {
            const imageKey = m.image_guid;
            const deleteButton = !skipDeleteIcon && <button className="button mini" type='button' onClick={() => { removeImage(imageKey); }}><icon>delete</icon></button >;
            return <div key={imageKey} className={className || "avatar-uploader"} >
                {/* <span>{anyFile ? (hideLink ? <></> : <a target='_blank' href={m.image_url}
                    >( m.name || m.image_guid )</a>) : <img src={m.image_url} style={{ width: '100%', objectFit: 'contain' }} alt="" />}</span> */}
                <span>{
                    anyFile ? (hideLink ? <></> : <a target='_blank' href={m.image_url} >( m.name || m.image_guid )</a>) /*eslint-disable-line react/jsx-no-target-blank*/ //NOSONAR
                    : <ImageWithAuth src={m.image_url} style={{ width: '100%', objectFit: 'contain' }} alt="" />}</span>
                {deleteButton}
            </div >
        }) : <></>}</>
}

CustomUpload.propTypes = {
    /** Field name in dataItem where image data will be stored */
    imageField: PropTypes.string.isRequired,
    /** The data object containing the image field */
    dataItem: PropTypes.object.isRequired,
    /** Function to update the dataItem state */
    setDataItem: PropTypes.func.isRequired,
    /** Type of upload (used in API endpoint) */
    type: PropTypes.string,
    /** Alt text for the image */
    alt: PropTypes.string,
    /** Whether to show the upload list */
    showUploadList: PropTypes.bool,
    /** Type of list display (e.g., "picture-card") */
    listType: PropTypes.string,
    /** Field name for file name display */
    fileNameField: PropTypes.string,
    /** CSS class name for styling */
    className: PropTypes.string,
    /** Custom action URL for upload */
    actionUrl: PropTypes.string,
    /** Whether multiple files can be uploaded */
    multiple: PropTypes.bool,
    /** Whether to hide the delete icon */
    skipDeleteIcon: PropTypes.bool,
    /** Whether to keep the original file name */
    keepName: PropTypes.bool,
    /** Whether to show the list of uploaded files */
    showList: PropTypes.bool,
    /** Whether to allow any file type (not just images) */
    anyFile: PropTypes.bool,
    /** Whether to hide the download link */
    hideLink: PropTypes.bool,
    /** Whether to use local upload (customRequest) instead of server upload */
    isLocalUpload: PropTypes.bool,
    /** Whether this is a manifest upload */
    isManifest: PropTypes.bool
};